import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Search, X, ExternalLink, RefreshCw } from "lucide-react";

const STATUSES = ["", "completed", "pending", "failed"];

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", plan: "", user_search: "" });
  const [drawerTxnId, setDrawerTxnId] = useState(null);

  const fetchList = useCallback(() => {
    setData(null);
    const params = { page, limit: 50 };
    if (filters.status) params.status = filters.status;
    if (filters.plan) params.plan = filters.plan;
    if (filters.user_search) params.user_search = filters.user_search;
    apiClient
      .get("/admin/payments", { params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Failed to load payments"));
  }, [page, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="admin-payments">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Payments</h1>
        <p className="text-sm text-slate-500 mt-1">View and manage payment transactions.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by user email"
            value={filters.user_search}
            onChange={(e) => setFilters((f) => ({ ...f, user_search: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") { setPage(1); fetchList(); }
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-payments-search"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="admin-payments-filter-status"
        >
          {STATUSES.map((s) => (
            <option key={s || "all"} value={s}>{s ? `Status: ${s}` : "All statuses"}</option>
          ))}
        </select>
        <select
          value={filters.plan}
          onChange={(e) => { setFilters((f) => ({ ...f, plan: e.target.value })); setPage(1); }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="admin-payments-filter-plan"
        >
          <option value="">All plans</option>
          <option value="basic">Basic</option>
          <option value="plus">Plus</option>
          <option value="pro">Pro</option>
        </select>
        <button
          onClick={() => { setPage(1); fetchList(); }}
          className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm"
          data-testid="admin-payments-apply"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm mb-4">{error}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {data === null ? (
          <TableSkeleton />
        ) : data.payments.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No payments match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-payments-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr
                    key={p.transaction_id}
                    onClick={() => setDrawerTxnId(p.transaction_id)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`admin-payments-row-${p.transaction_id}`}
                  >
                    <td className="px-4 py-3 font-medium text-slate-800 truncate max-w-[220px]">{p.user_email}</td>
                    <td className="px-4 py-3"><PlanBadge plan={p.plan} /></td>
                    <td className="px-4 py-3">${(p.amount ?? 0).toFixed(2)} {(p.currency || "usd").toUpperCase()}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {p.is_subscription ? "Subscription" : "One-time"}
                      {p.is_annual ? " (Annual)" : ""}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[180px]">{p.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data !== null && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>{data.total} transactions · page {data.page}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40" data-testid="admin-payments-prev-page">Previous</button>
            <button disabled={page * data.limit >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40" data-testid="admin-payments-next-page">Next</button>
          </div>
        </div>
      )}

      {drawerTxnId && (
        <PaymentDrawer
          transactionId={drawerTxnId}
          onClose={() => setDrawerTxnId(null)}
          onAction={fetchList}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const safe = typeof status === "string" && status.length > 0 ? status : "unknown";
  const colors = {
    completed: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
    unknown: "bg-slate-100 text-slate-600",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${colors[safe] || colors.unknown}`}>{safe}</span>;
}

function PlanBadge({ plan }) {
  const safe = typeof plan === "string" && plan.length > 0 ? plan : "—";
  const colors = {
    basic: "bg-blue-100 text-blue-700",
    plus: "bg-violet-100 text-violet-700",
    pro: "bg-amber-100 text-amber-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${colors[safe] || "bg-slate-100 text-slate-600"}`}>{safe}</span>;
}

function fmtDate(v) {
  if (typeof v !== "string" || v.length < 10) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v.slice(0, 10); }
}

function TableSkeleton() {
  return (
    <div className="p-2" data-testid="admin-payments-loading">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-12 bg-slate-100 rounded animate-pulse my-2 mx-2" />
      ))}
    </div>
  );
}

// ---------- Payment Drawer ---------------------------------------------------

function PaymentDrawer({ transactionId, onClose, onAction }) {
  const [detail, setDetail] = useState(null);
  const [showReconcile, setShowReconcile] = useState(false);

  const fetchDetail = useCallback(() => {
    setDetail(null);
    apiClient
      .get(`/admin/payments/${transactionId}`)
      .then((res) => setDetail(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load transaction"));
  }, [transactionId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const ready = typeof detail === "object" && detail !== null && detail.transaction;
  const txn = ready ? detail.transaction : null;
  const user = ready ? detail.user : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-payment-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-xl">
        <header className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">Transaction</div>
            <div className="font-semibold text-slate-900 truncate font-mono text-sm">
              {ready ? txn.transaction_id : <span className="inline-block h-4 w-48 bg-slate-200 rounded animate-pulse" />}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="admin-payment-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {!ready ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Actions */}
            {txn.status === "pending" && (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowReconcile(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-violet-600 text-white rounded-lg text-sm"
                  data-testid="admin-payment-reconcile-btn"
                >
                  <RefreshCw className="w-4 h-4" /> Reconcile
                </button>
              </div>
            )}

            {/* Transaction details */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Transaction Details</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field k="Transaction ID" v={txn.transaction_id} mono />
                <Field k="Status" v={<StatusBadge status={txn.status} />} />
                <Field k="Amount" v={`$${(txn.amount ?? 0).toFixed(2)} ${(txn.currency || "USD").toUpperCase()}`} />
                <Field k="Plan" v={<PlanBadge plan={txn.plan} />} />
                <Field k="Type" v={txn.is_subscription ? "Subscription" : "One-time"} />
                <Field k="Billing" v={txn.is_annual ? "Annual" : "Monthly"} />
                <Field k="Created" v={fmtDate(txn.created_at)} />
                <Field k="Completed" v={fmtDate(txn.completed_at)} />
                {txn.session_id && (
                  <div className="col-span-2">
                    <dt className="text-xs text-slate-500">Stripe Session</dt>
                    <dd className="font-mono text-xs text-slate-800 break-all">{txn.session_id}</dd>
                  </div>
                )}
                {txn.stripe_subscription_id && (
                  <div className="col-span-2">
                    <dt className="text-xs text-slate-500">Stripe Subscription</dt>
                    <dd className="font-mono text-xs text-slate-800 break-all">{txn.stripe_subscription_id}</dd>
                  </div>
                )}
                {txn.failure_reason && <Field k="Failure Reason" v={txn.failure_reason} />}
                {txn.note && (
                  <div className="col-span-2">
                    <dt className="text-xs text-slate-500">Note</dt>
                    <dd className="text-sm text-slate-800 bg-amber-50 border border-amber-200 rounded-lg p-2">{txn.note}</dd>
                  </div>
                )}
                {txn.manual_reconcile_at && <Field k="Reconciled At" v={fmtDate(txn.manual_reconcile_at)} />}
              </dl>
            </section>

            {/* User info */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">User</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field k="Email" v={user?.email || "—"} />
                <Field k="User ID" v={user?.user_id || "—"} mono />
                <Field k="Current Tier" v={user?.tier || "—"} />
                <Field k="Account Status" v={user?.status || "—"} />
              </dl>
            </section>

            {/* Stripe links */}
            {txn.session_id && (
              <section>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Stripe Links</h3>
                <div className="space-y-2">
                  <a
                    href={`https://dashboard.stripe.com/payments/${txn.session_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800"
                    data-testid="admin-payment-stripe-link"
                  >
                    <ExternalLink className="w-4 h-4" /> View in Stripe Dashboard
                  </a>
                </div>
              </section>
            )}
          </div>
        )}

        {showReconcile && ready && (
          <ReconcileModal
            txn={txn}
            onClose={() => setShowReconcile(false)}
            onDone={() => {
              setShowReconcile(false);
              fetchDetail();
              onAction();
            }}
          />
        )}
      </aside>
    </div>
  );
}

function Field({ k, v, mono = false }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{k}</dt>
      <dd className={`text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>
        {typeof v === "string" || typeof v === "number" ? v : v || "—"}
      </dd>
    </div>
  );
}

function ReconcileModal({ txn, onClose, onDone }) {
  const [newStatus, setNewStatus] = useState("completed");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason is required (min 3 chars)");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.patch(`/admin/payments/${txn.transaction_id}/reconcile`, {
        new_status: newStatus,
        reason,
      });
      toast.success(`Transaction marked as ${newStatus}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconciliation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="admin-reconcile-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Reconcile Transaction</h2>
        <p className="text-xs text-slate-500 mb-4 font-mono">{txn.transaction_id}</p>

        <div className="mb-4">
          <label className="text-xs text-slate-500 mb-1 block">Current status: <StatusBadge status={txn.status} /></label>
        </div>

        <div className="mb-4">
          <label className="text-xs text-slate-500 mb-1 block">New status</label>
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-reconcile-status-select"
          >
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="text-xs text-slate-500 mb-1 block">Reason (required)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Explain why this transaction is being reconciled..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-reconcile-reason"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-reconcile-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm"
            data-testid="admin-reconcile-confirm"
          >
            {submitting ? "Processing..." : "Reconcile"}
          </button>
        </div>
      </div>
    </div>
  );
}
